import React, { useState, useEffect, useContext, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState, useAppDispatch } from '../../store';
import { useHistory, useLocation } from 'react-router-dom';
import Header from '../shared/TailwindComponents/Header';
import FilterBarWithTabs from '../shared/TailwindComponents/FilterBarWithTabs';
import Table from '../shared/TailwindComponents/Table';
import Badge from '../shared/TailwindComponents/Badge';
import { LayoutContext } from '../../contexts/LayoutContext';
import { useBreadcrumbs } from '../../contexts/BreadcrumbsContext';
import { parseFileNavigationPath, buildFileNavigationPath, updateFileInPath, removeFileFromPath } from '../../utils/fileNavigationUtils';
import { formatDate, replaceUnderscoresWithSpaces, formatDocumentName, formatDateTime } from '../../utils/functions';
import { BreadcrumbService } from '../../services/breadcrumbService';
import {
  PiTrashLight,
  PiFolderDashedLight,
  PiMagnifyingGlassLight,
  PiPlusLight,
  PiCloudArrowUpLight,
  PiUsersThreeLight,
  PiShareNetworkLight,
  PiWarningCircleLight,
  PiClockCounterClockwiseLight,
  PiPencilLight,
} from 'react-icons/pi';
import { FileProps, FolderProps } from './documents/types';
import WordEditor from './documents/WordEditor';
import CreateFolderForm from './CreateFolderForm';
import CreateFolderWithFilesForm from './CreateFolderWithFilesForm';
import Trash from './Trash';
import { PiRecycleFill, PiMicrosoftWordLogoLight, PiGoogleLogoLight } from 'react-icons/pi';
import { useMicrosoftGraph } from '../../contexts/MicrosoftGraphContext';
import { useGoogleDrive } from '../../contexts/GoogleContext';
import TagPicker from './TagPicker';
import AuditLogPanel from '../AuditLog/AuditLogPanel';
import {
  deleteFile,
  deleteFolder,
  renameFolder
} from '../../store/actions/FolderAction';
import { renameFile, streamFile } from '../../store/actions/DocumentsAction';
import { hideModal, showModal } from '../../store/actions/modalActions';
import { showFeedbackModal } from '../../store/actions/UserFeedbackActions';
import { ShareUser } from '../Templates/BrandingSection';
import { fetchAllEmployees } from '../../store/actions/organization/employeeActions';
import { getInitials } from '../shared/TailwindComponents/CollaboratorsBadge';
import CollaboratorStack from '../shared/access/CollaboratorStack';
import { FileGlyph, FolderGlyph, getFileKind, getKindBadgeColor } from './fileGlyphs';
import { Tooltip } from '@mui/material';
import Input from '../shared/TailwindComponents/Input';
import Button from '../shared/TailwindComponents/Button';
import { fetchRootFiles, fetchFolderContents, FileApiResponse } from '../../utils/FileApiBuilder';
import { useLicenseGate } from '../../hooks/useLicenseGate';
import { useFeatureTour } from '../../hooks/useFeatureTour';
import { FeatureTour, filesListTourSteps } from '../shared/FeatureTour';

import ActivityDrawer from '../shared/activity/ActivityDrawer';

// Rename modal
const RenameModalContent: React.FC<{
  initialValue: string;
  placeholder: string;
  onConfirm: (newValue: string) => void;
  t: any;
}> = ({ initialValue, placeholder, onConfirm, t }) => {
  const [inputValue, setInputValue] = useState(initialValue);

  return (
    <div className="px-4 space-y-4">
      <Input
        type="text"
        placeholder={placeholder}
        value={inputValue}
        autoFocus
        onChange={(e) => setInputValue(e.target.value)}
      />
      <Button variant="primary" size="small" onClick={() => onConfirm(inputValue)} className='ml-auto'>
        {t('Rename')}
      </Button>
    </div>
  );
};

interface FilesProps {
  clientDocs?: FolderProps[];
  clientDocLoading?: boolean;
}

const Files: React.FC<FilesProps> = ({ clientDocs, clientDocLoading }) => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const location = useLocation();
  const { t } = useTranslation();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const { isSignedIn: isMsSignedIn, MSALsignIn, uploadFileToOneDrive, getEditLink } = useMicrosoftGraph();
  const { isSignedIn: isGoogleSignedIn, signIn: googleSignIn, handleOpenPicker } = useGoogleDrive();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { checkSectionGate } = useLicenseGate();

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'files-list',
    steps: filesListTourSteps,
    autoStartDelay: 600,
  });

  // State - core data
  const [isLoading, setIsLoading] = useState(false);
  const [displayedItems, setDisplayedItems] = useState<any[]>([]);
  const [selectedFile, setSelectedFile] = useState<FileProps | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [showTrash, setShowTrash] = useState(false);
  const [trashBulkActions, setTrashBulkActions] = useState<{ label: string; onClick: () => void; icon?: React.ReactElement }[]>([]);
  const [trashSelectedCount, setTrashSelectedCount] = useState(0);
  const emptyTrashRef = useRef<(() => void) | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [auditTarget, setAuditTarget] = useState<{ type: 'file' | 'folder'; id: string; name: string } | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [pathStack, setPathStack] = useState<Array<{ id: string; name: string }>>([]);
  const [navigationState, setNavigationState] = useState<any>(null); 
  
  // Pagination state
  const [pagination, setPagination] = useState<{
    page: number;
    limit: number;
    total_items: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  }>({ page: 1, limit: 50, total_items: 0, total_pages: 0, has_next: false, has_previous: false });
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  
  // Cache folder names to use in breadcrumbs
  // When we navigate to a folder, we cache its name so we can display it later
  const [folderNameCache, setFolderNameCache] = useState<{ [key: string]: string }>({});
  
  // Track the current folder being navigated to (for breadcrumb "Loading..." state)
  const [navigatingToFolderId, setNavigatingToFolderId] = useState<string>('');

  // Search & Sort state
  const [searchTerm, setSearchTerm] = useState<string>('');
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [sortBy, setSortBy] = useState<string>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }
    };
  }, []);

  // User data
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const { employees } = useSelector((state: RootState) => state.employees);
  const [firmEmployees, setFirmEmployees] = useState<ShareUser[]>([]);
  const userInfo = useSelector((state: RootState) => state.user.userInfo);

  // Fetch employees
  useEffect(() => {
    if (!employees || employees.length === 0) {
      dispatch(fetchAllEmployees({ sortOrder: 'desc' }));
    }
  }, []);

  // Build firm employees list only when needed (memoized)
  const buildFirmEmployees = useCallback(() => {
    if (employees && employees.length > 0) {
      return employees
        .filter((f) => f.UserID !== userInfo.UserID)
        .map((e) => ({
          id: e.UserID,
          name: `${e.User?.FirstName || e.FirstName || ''} ${e.User?.LastName || e.LastName || ''}`,
          email: e.User?.Email || e.Email || '',
          role: e.role || e.Role || '',
          avatarUrl: e.User?.ProfilePicture || e.ProfilePicture || null,
          initials: getInitials(`${e.User?.FirstName || e.FirstName || ''} ${e.User?.LastName || e.LastName || ''}`)
        }));
    }
    return [];
  }, [employees, userInfo.UserID]);

  // Update firmEmployees only when employees change
  useEffect(() => {
    setFirmEmployees(buildFirmEmployees());
  }, [buildFirmEmployees]);

  // Parse URL to restore file selection
  useEffect(() => {
    const navigationState = parseFileNavigationPath(location.pathname, location.search);
    const params = new URLSearchParams(location.search);
    const isViewingFile = params.get('type') === 'file';

    if (navigationState?.fileId && isViewingFile) {
      const fileToSelect = displayedItems.find(f => f.FileID === navigationState.fileId);
      if (fileToSelect && (!selectedFile || selectedFile.FileID !== fileToSelect.FileID)) {
        setSelectedFile(fileToSelect);
        setEditorOpen(true);
      }
    } else {
      if (editorOpen && selectedFile) {
        setEditorOpen(false);
        setSelectedFile(null);
      }
    }
  }, [location.pathname, location.search, displayedItems]);

useEffect(() => {
  const parsedState = parseFileNavigationPath(location.pathname, location.search);
  const params = new URLSearchParams(location.search);
  const isViewingFileFromUrl = params.get('type') === 'file';
  
  setNavigationState(parsedState); 
  
  let folderId: string = parsedState?.folderId || '';
  let parentFolderId: string | undefined = parsedState?.parentFolderId;
  let fileId: string | undefined = parsedState?.fileId;
  let isViewingFile: boolean = isViewingFileFromUrl;

  // Special handling for root-level files
  if (parsedState?.pathStack.length! > 0 && parsedState!.pathStack[0].type === 'file') {
    folderId = ''; // Reset to fetch root files
  }

  const fetchData = async () => {
    try {
      setIsLoading(true);
      // Track that we're navigating to this folder (for breadcrumb loading state)
      setNavigatingToFolderId(folderId || '');
      
      let res: FileApiResponse | undefined;

      const currentPage = new URLSearchParams(location.search).get('page') || '1';
      
      if (folderId) {
        res = await fetchFolderContents(folderId, parseInt(currentPage), parentFolderId, { searchTerm, sortBy, sortOrder });
      } else {
        res = await fetchRootFiles({ page: parseInt(currentPage), searchTerm, sortBy, sortOrder });
      }

      if (res) {
        // Use interleaved 'items' if available, otherwise combine folders+files
        const folders = res.folders || res.subfolders || [];
        const files = res.files || [];
        const items = res.items || [...folders, ...files];

        setDisplayedItems(items);

        // Update breadcrumb service cache with hierarchy data from API
        if (res.hierarchy) {
          BreadcrumbService.updateHierarchyCache(res.hierarchy);
        }

        // Store pagination info
        if (res.pagination) {
          setPagination({
            page: res.pagination.page || 1,
            limit: res.pagination.limit || 15,
            total_items: res.pagination.total_items || 0,
            total_pages: res.pagination.total_pages || 0,
            has_next: res.pagination.has_next || false,
            has_previous: res.pagination.has_previous || false,
          });
        }

        // Build path from ParentChain
        const newPathStack: Array<{ id: string; name: string }> = [];
        let parentChain = (files.length > 0 && files[0].ParentChain) 
          ? files[0].ParentChain 
          : (folders.length > 0 && folders[0].ParentChain ? folders[0].ParentChain : []);
        
        // If currentFolder has ParentChain, use that as it includes all ancestors
        if (res.currentFolder && res.currentFolder.ParentChain) {
          parentChain = res.currentFolder.ParentChain;
        }
        
        // Add all parent items to pathStack
        if (parentChain && Array.isArray(parentChain)) {
          parentChain.forEach((item: any) => {
            newPathStack.push({ id: item.id, name: item.name });
            // Cache items for breadcrumb service and future lookups
            BreadcrumbService.setCacheEntry(item.id, item.name);
          });
        }
        
        if (folderId) {
          // Check if current folder is already in the chain
          const currentFolderAlreadyInChain = parentChain?.some((item: any) => item.id === folderId);
          
          if (!currentFolderAlreadyInChain) {
            // Try to get current folder name from API response first
            let currentFolderName = (res.currentFolder?.name || res.currentFolder?.FolderName);
            
            // Fall back to cache if API didn't provide it
            if (!currentFolderName) {
              currentFolderName = BreadcrumbService.getCachedName(folderId);
            }
            
            // Also cache via service for future reference
            if (currentFolderName) {
              BreadcrumbService.setCacheEntry(folderId, currentFolderName);
            }
            
            // Only add if we have a name (avoid showing UUIDs)
            if (currentFolderName) {
              newPathStack.push({ id: folderId, name: currentFolderName });
            }
          }
        }
        
        setPathStack(newPathStack);
        // Store navigation state with ParentChain for breadcrumb service
        setNavigationState({ ...parsedState, parentChain });

        // If viewing a file on direct URL refresh, find and select the file from the fetched list
        if (isViewingFile && fileId) {
          const fileToSelect = items.find(f => f.FileID === fileId);
          if (fileToSelect) {
            fileToSelect.FolderID = folderId || '';
            if (parentFolderId) {
              fileToSelect.ParentFolderID = parentFolderId;
            }
            setSelectedFile(fileToSelect);
            setEditorOpen(true);
          }
        }
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setIsLoading(false);
      // Clear the navigation tracking once done
      setNavigatingToFolderId('');
      // Clean up refresh param from URL (if present) without triggering re-render
      const cleanupParams = new URLSearchParams(location.search);
      if (cleanupParams.has('_r')) {
        cleanupParams.delete('_r');
        const cleanSearch = cleanupParams.toString();
        const cleanUrl = `${location.pathname}${cleanSearch ? `?${cleanSearch}` : ''}`;
        window.history.replaceState(null, '', cleanUrl);
      }
    }
  };

  if (!clientDocs) {
    fetchData();
  }
}, [location.pathname, location.search, clientDocs, folderNameCache, searchTerm, sortBy, sortOrder, refreshKey]);

    // Helper function to derive current folder ID from pathStack (single source of truth)
  const getCurrentFolderId = useCallback((): string => {
    // Use pathStack to derive folder ID - most recent item is current folder
    if (pathStack.length > 0) {
      return pathStack[pathStack.length - 1].id;
    }
    return ''; // Root folder has no ID
  }, [pathStack]);

  // Helper function to get parent folder ID from pathStack
  const getParentFolderId = useCallback((): string | undefined => {
    // Parent is the second-to-last item in pathStack
    if (pathStack.length > 1) {
      return pathStack[pathStack.length - 2].id;
    }
    return undefined;
  }, [pathStack]);

  // Update breadcrumbs whenever path or file selection changes
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const isViewingFile = params.get('type') === 'file';
    
    let formattedBreadcrumbs: any[] = [];
    
    // When viewing a file with parent folder info, show simple breadcrumb
    if (isViewingFile && selectedFile && selectedFile.ParentFolderName) {
      formattedBreadcrumbs = [
        { name: 'Files', href: '/files', current: false },
        { name: replaceUnderscoresWithSpaces(selectedFile.ParentFolderName || ''), href: `/files/${selectedFile.ParentFolderID}`, current: false },
        { name: selectedFile.FileName.replace(/\.[^.]+$/, ''), href: '', current: true }
      ];
    } else {
      // Use BreadcrumbService for folder browsing
      let parentChain = navigationState?.parentChain || selectedFile?.ParentChain || (pathStack.length > 0 ? pathStack.map(p => ({ id: p.id, name: p.name })) : null);
      
      // When viewing a file and parentChain is empty but we have ParentFolderName, construct it from the file response
      if (isViewingFile && selectedFile && (!parentChain || parentChain.length === 0)) {
        if (selectedFile.ParentFolderName && selectedFile.ParentFolderID) {
          parentChain = [{ id: selectedFile.ParentFolderID, name: selectedFile.ParentFolderName }];
          // Cache the parent folder name so BreadcrumbService has it
          BreadcrumbService.setCacheEntry(selectedFile.ParentFolderID, selectedFile.ParentFolderName);
        }
      }
      
      // When viewing a file, show the file name as the last breadcrumb instead of the folder
      const fileNameForBreadcrumb = (selectedFile && isViewingFile) ? selectedFile.FileName.replace(/\.[^.]+$/, '') : undefined;
      const folderNameForBreadcrumb = (getCurrentFolderId() && !isViewingFile) ? pathStack[pathStack.length - 1]?.name : undefined;
      
      const breadcrumbs = BreadcrumbService.buildBreadcrumbs(
        navigationState,
        parentChain,
        folderNameForBreadcrumb,
        fileNameForBreadcrumb
      );

      // Convert BreadcrumbService format to context format
      formattedBreadcrumbs = breadcrumbs.map(bc => ({
        name: replaceUnderscoresWithSpaces(bc.name || ''),
        href: bc.href,
        current: bc.isCurrent
      })) as any;
    }

    // Update the last breadcrumb to show "Loading..." if we're navigating
    if (navigatingToFolderId && formattedBreadcrumbs.length > 0) {
      formattedBreadcrumbs[formattedBreadcrumbs.length - 1].name = 'Loading...';
    }

    setBreadcrumbs(formattedBreadcrumbs);
  }, [pathStack, selectedFile, editorOpen, setBreadcrumbs, getCurrentFolderId, navigationState, navigatingToFolderId, location.search]);

  // Set scrollable
  useEffect(() => {
    setIsContentScrollable(true);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);


  // Handlers
  const handleFolderClick = (folder: FolderProps) => {
    // Cache the folder name so we can use it in breadcrumbs
    setFolderNameCache(prev => ({ ...prev, [folder.FolderID]: folder.FolderName }));
    // Build nested path: /files/{id1}/{id2}/{id3}...
    const currentPath = location.pathname.replace(/\/$/, ''); // Remove trailing slash
    const newPath = `${currentPath}/${folder.FolderID}`;
    history.push(newPath);
  };

  const handleFileClick = (file: FileProps) => {
    setSelectedFile(file);
    setEditorOpen(true);
    // Build navigation path using the utility to properly handle root-level files
    const currentFolderId = getCurrentFolderId();
    const parentFolderId = getParentFolderId();
    const newPath = buildFileNavigationPath(currentFolderId || undefined, parentFolderId, file.FileID);
    history.push(newPath);
  };

  const handleRenameFolder = (folder: FolderProps) => {
    dispatch(
      showModal({
        type: 'custom',
        title: t('Rename Folder'),
        message: t('Rename Folder'),
        icon: <PiPencilLight className="w-6 h-6 text-fg-body" />,
        showModal: true,
        content: (
          <RenameModalContent
            initialValue={folder.FolderName}
            placeholder={t('New Folder Name')}
            t={t}
            onConfirm={async (newName) => {
              const trimmed = newName.trim();
              if (trimmed && trimmed !== folder.FolderName) {
                try {
                  await dispatch(renameFolder(folder.FolderID, trimmed));
                  setDisplayedItems(prev =>
                    prev.map(f => f.FolderID === folder.FolderID ? { ...f, FolderName: trimmed } : f)
                  );
                  setPathStack(prev =>
                    prev.map(p => p.id === folder.FolderID ? { ...p, name: trimmed } : p)
                  );
                  dispatch(hideModal());
                } catch (err) {
                  console.error('Rename error:', err);
                }
              }
            }}
          />
        )
      })
    );
  };

  const handleRenameFile = (file: FileProps) => {
    const nameWithoutExt = file.FileName.replace(/\.[^.]+$/, '');
    const ext = file.FileName.match(/\.[^.]+$/) ? file.FileName.match(/\.[^.]+$/)![0] : '';

    dispatch(
      showModal({
        type: 'custom',
        title: t('Rename File'),
        message: t('Rename File'),
        icon: <PiPencilLight className="w-6 h-6 text-fg-body" />,
        showModal: true,
        content: (
          <RenameModalContent
            initialValue={nameWithoutExt}
            placeholder={t('New File Name')}
            t={t}
            onConfirm={async (newName) => {
              const trimmed = newName.trim();
              if (trimmed && trimmed !== nameWithoutExt) {
                try {
                  const newFileName = trimmed + ext;
                  await dispatch(renameFile(file.FileID, newFileName, file.FolderID || getCurrentFolderId()));
                  setDisplayedItems(prev =>
                    prev.map(f => f.FileID === file.FileID ? { ...f, FileName: newFileName } : f)
                  );
                  if (selectedFile?.FileID === file.FileID) {
                    setSelectedFile(prev => prev ? { ...prev, FileName: newFileName } : prev);
                  }
                  dispatch(hideModal());
                } catch (err) {
                  console.error('Rename error:', err);
                }
              }
            }}
          />
        )
      })
    );
  };

  const handleDeleteFolder = async (folder: FolderProps) => {
    // Guard: prevent deleting system/managed folders
    if (folder.IsSystemFolder || folder.IsManagedFolder ||
        folder.FolderName?.toLowerCase() === 'matters' ||
        folder.FolderName?.toLowerCase() === 'clients') {
      // `warning` has no body renderer of its own — the shared modal header
      // prints the headline and `subText` — so the explanation goes in both
      // the canonical `message` and `subText`.
      const reason = t('This is a system-managed folder and cannot be deleted.');
      dispatch(
        showModal({
          type: 'warning',
          title: t('Cannot Delete Folder'),
          message: reason,
          subText: reason,
          variant: 'warning',
          icon: <PiWarningCircleLight className="w-6 h-6 text-warning-500" />,
          onConfirm: () => dispatch(hideModal()),
          onCancel: () => {},
          showModal: true
        })
      );
      return;
    }
    dispatch(
      showModal({
        type: 'confirmation',
        title: t('Delete Folder'),
        message: t('This folder and all its contents will be moved to the trash.'),
        subText: t('You can restore it within 30 days.'),
        variant: 'danger',
        confirmLabel: t('Delete'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        isDest: true,
        onConfirm: async () => {
          setIsLoading(true);
          try {
            const success = await dispatch(deleteFolder([folder.FolderID], async () => {}) as any);
            if (success) {
              setDisplayedItems(prev => prev.filter(f => f.FolderID !== folder.FolderID));
            }
          } finally {
            setIsLoading(false);
            dispatch(hideModal());
          }
        },
        onCancel: () => {},
        showModal: true
      })
    );
  };

  const handleDeleteFile = async (file: FileProps) => {
    dispatch(
      showModal({
        type: 'confirmation',
        title: t('Delete File'),
        message: t('This file will be moved to the trash.'),
        subText: t('You can restore it within 30 days.'),
        variant: 'danger',
        confirmLabel: t('Delete'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        isDest: true,
        onConfirm: async () => {
          setIsLoading(true);
          try {
            const success = await dispatch(deleteFile([file.FileID], async () => {}, file.FolderID || getCurrentFolderId()) as any);
            if (success) {
              setDisplayedItems(prev => prev.filter(f => f.FileID !== file.FileID));
              if (selectedFile?.FileID === file.FileID) {
                setEditorOpen(false);
                setSelectedFile(null);
              }
            }
          } finally {
            setIsLoading(false);
            dispatch(hideModal());
          }
        },
        onCancel: () => {},
        showModal: true
      })
    );
  };

  const handleDeleteSelected = async () => {
    // Filter out protected folders from selection
    const protectedIds = selectedItems.filter(id => {
      const row = tableData.find(r => r.id === id);
      return row && isProtectedFolder(row);
    });

    if (protectedIds.length > 0 && protectedIds.length === selectedItems.length) {
      const reason = t('The selected items include system-managed folders that cannot be deleted.');
      dispatch(
        showModal({
          type: 'warning',
          title: t('Cannot Delete Items'),
          message: reason,
          subText: reason,
          variant: 'warning',
          icon: <PiWarningCircleLight className="w-6 h-6 text-warning-500" />,
          onConfirm: () => dispatch(hideModal()),
          onCancel: () => {},
          showModal: true
        })
      );
      return;
    }

    const deletableItems = selectedItems.filter(id => !protectedIds.includes(id));

    dispatch(
      showModal({
        type: 'confirmation',
        title: t('Delete Selected Items'),
        message: protectedIds.length > 0
          ? t('Some selected items are system-managed and will be skipped. The remaining items will be moved to the trash.')
          : t('The selected items will be moved to the trash.'),
        subText: t('You can restore them within 30 days.'),
        variant: 'danger',
        confirmLabel: t('Delete'),
        icon: <PiTrashLight className="w-6 h-6 text-danger-600" />,
        isDest: true,
        onConfirm: async () => {
          setIsLoading(true);
          try {
            const currentFolderId = getCurrentFolderId();
            const folderIds = deletableItems.filter(id => tableData.find(row => row.id === id && row.isFolder)).map(id => id);
            const fileIds = deletableItems.filter(id => tableData.find(row => row.id === id && !row.isFolder)).map(id => id);

            let deletedFolderIds: string[] = [];
            let deletedFileIds: string[] = [];

            if (folderIds.length > 0) {
              const folderSuccess = await dispatch(deleteFolder(folderIds, async () => {}) as any);
              if (folderSuccess) {
                deletedFolderIds = folderIds;
                setDisplayedItems(prev => prev.filter(f => !f.FolderID || !folderIds.includes(f.FolderID)));
              }
            }

            if (fileIds.length > 0) {
              const fileSuccess = await dispatch(deleteFile(fileIds, async () => {}, currentFolderId) as any);
              if (fileSuccess) {
                deletedFileIds = fileIds;
                setDisplayedItems(prev => prev.filter(f => !f.FileID || !fileIds.includes(f.FileID)));
              }
            }

            // Only clear selections for successfully deleted items
            const deletedIds = [...deletedFolderIds, ...deletedFileIds];
            setSelectedItems(prev => prev.filter(id => !deletedIds.includes(id)));
          } finally {
            setIsLoading(false);
            dispatch(hideModal());
          }
        },
        onCancel: () => {},
        showModal: true
      })
    );
  };

  const handleManageAccess = (item: FolderProps | FileProps, isFolder: boolean) => {
    const itemId = isFolder ? (item as FolderProps).FolderID : (item as FileProps).FileID;
    const itemName = isFolder ? (item as FolderProps).FolderName : (item as FileProps).FileName;
    const itemCollaborators = item.Collaborators || [];

    // Convert collaborators to the format expected by CollaboratorSelector
    const formattedCollaborators = itemCollaborators.map(collab => ({
      user_id: collab.user_uuid || collab.id,
      collaborator_uuid: collab.user_uuid || collab.id,
      access: (collab.access || collab.role || 'view') as 'edit' | 'view',
    }));

    // Find the creator ID (first collaborator is typically the creator)
    const creatorId = itemCollaborators.length > 0 ? (itemCollaborators[0].user_uuid || itemCollaborators[0].id) : undefined;

    dispatch(
      showModal({
        type: 'manageAccess',
        title: t('Manage Access'),
        message: t('Add or remove collaborators who can access this') + (isFolder ? ' ' + t('folder') : ' ' + t('file')),
        icon: <PiShareNetworkLight className="w-6 h-6 text-fg-body" />,
        itemId,
        itemType: isFolder ? 'folder' : 'file',
        itemName,
        itemCollaborators: formattedCollaborators,
        creatorId,
        showModal: true
      })
    );
  };

  /**
   * Tag edits land in state, keyed by id.
   *
   * This used to mutate the fetched object in place (`file.Tags = tags`) and
   * bump `refreshKey`, which re-ran the fetch — so the new tag depended on a
   * mutation surviving a refetch that replaced the very object it was written
   * to, and the cell went back to what it was. The assign response IS the
   * item's new tag list, so it is written to `displayedItems` directly and no
   * refetch is needed.
   */
  const handleTagsChanged = useCallback(
    (target: { fileId?: string; folderId?: string }, tags: any[]) => {
      setDisplayedItems(prev => prev.map(item => {
        if (target.fileId && item.FileID === target.fileId) return { ...item, Tags: tags };
        if (target.folderId && !item.FileID && item.FolderID === target.folderId) return { ...item, Tags: tags };
        return item;
      }));
    },
    []
  );

  // Build table data
  const isOrgWideFolder = (name: string) => ['matters', 'clients'].some(n => name.toLowerCase() === n.toLowerCase());

  const enrichShareWithData = (collaborators: any[] | undefined): ShareUser[] => {
    if (!collaborators || !Array.isArray(collaborators)) return [];
    return collaborators
      .map(collab => {
        // API now returns user_uuid, name, email, role, avatar_url directly
        const userId = collab.user_uuid || collab.id;
        const name = collab.name;
        const email = collab.email;
        const role = collab.role || collab.access || '';
        const avatarUrl = collab.avatar_url || collab.avatarUrl || null;

        if (userId && name && email) {
          return { id: userId, name, email, initials: getInitials(name), avatarUrl, role };
        }
        return null;
      })
      .filter(Boolean) as ShareUser[];
  };

  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'N/A';
    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024;
      unitIndex++;
    }
    return `${size.toFixed(1)} ${units[unitIndex]}`;
  };

  // Helper: detect if an item belongs to the Matters hierarchy (folder or contents)
  const isMattersContext = pathStack.some(p => p.name.toLowerCase() === 'matters');
  const isProtectedFolder = (row: any): boolean => {
    // System folders (Matters, Clients root) — protected from delete/rename
    if (row.isFolder && (row.doc.IsSystemFolder || row.doc.IsManagedFolder)) return true;
    // Fallback: name-based check for legacy data without flags
    if (row.isFolder && row.doc.FolderName?.toLowerCase() === 'matters') return true;
    if (row.isFolder && row.doc.FolderName?.toLowerCase() === 'clients') return true;
    // Currently browsing inside a Matters folder (matters context disables actions on children)
    if (isMattersContext) return true;
    // Item has a ParentChain containing Matters
    const chain = row.doc.ParentChain || [];
    if (chain.some((p: any) => (p.name || p.FolderName || '').toLowerCase() === 'matters')) return true;
    return false;
  };

  const isSearching = !!searchTerm;
  const columns = isSearching
    ? ['Select', 'Name', 'Location', 'Type', 'Tags', 'Has Access', 'Last Modified', 'Actions']
    : ['Select', 'Name', 'Type', 'Tags', 'Has Access', 'Last Modified', 'Actions'];
  const tableData = displayedItems.map((item) => {
    const isFolder = !item.FileID && !!item.FolderName;
    const collaborators = enrichShareWithData(item.Collaborators as any);

    // One overlapping-avatar stack for folders and files alike. This was two
    // copies of the same 35 lines of hand-rolled avatars plus a `+N` chip in a
    // raw `bg-gray-200 dark:bg-gray-700` circle — the shared stack is the only
    // thing in the app that draws faces, and it flips with the theme.
    const accessCell = collaborators.length > 0 ? (
      <div className="flex items-center">
        <CollaboratorStack collaborators={collaborators} max={3} size="sm" withAccessLabels={false} />
      </div>
    ) : null;

    // Location cell (only populated during search, when items may be nested)
    const locationPath: string = item.Location || '';
    const locationFolderId: string = item.LocationFolderID || '';
    const locationCell = locationPath && locationPath !== 'Root' ? (
      <Tooltip title={locationPath} arrow placement="top">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (locationFolderId) {
              history.push(`/files/${locationFolderId}`);
            }
          }}
          className="text-sm text-fg-body hover:text-champagne hover:underline truncate max-w-[200px] inline-block text-left cursor-pointer transition-colors"
        >
          {locationPath}
        </button>
      </Tooltip>
    ) : (
      <span className="text-sm text-fg-muted">{t('Root')}</span>
    );

    if (isFolder) {
      const folder = item as FolderProps;
      return {
        id: folder.FolderID,
        doc: folder,
        Select: null,
        Name: (
          <div className="flex items-center gap-2 min-w-0">
            <FolderGlyph folderName={folder.FolderName} className="w-5 h-5 flex-shrink-0" />
            <span className="truncate text-fg-strong" title={replaceUnderscoresWithSpaces(folder.FolderName || '')}>
              {replaceUnderscoresWithSpaces(folder.FolderName || '')}
            </span>
          </div>
        ),
        Type: <Badge color="blue" className="text-xs whitespace-nowrap">{t('Folder')}</Badge>,
        Tags: (
          <TagPicker
            tags={folder.Tags || []}
            folderId={folder.FolderID}
            onTagsChanged={(tags) => handleTagsChanged({ folderId: folder.FolderID }, tags)}
          />
        ),
        'Last Modified': formatDateTime(folder.ModifiedAt),
        FolderType: folder.FolderType,
        ...(isSearching ? { Location: locationCell } : {}),
        'Has Access': accessCell,
        isFolder: true,
      };
    } else {
      const file = item as FileProps;
      const fileExt = getFileKind(file.FileName, file.FileType);
      const { name: formattedName, extension } = formatDocumentName(file.FileName);

      return {
        id: file.FileID,
        doc: file,
        Select: null,
        Name: (
          <div className="flex items-center gap-2 min-w-0">
            <FileGlyph
              fileName={file.FileName}
              mimeType={file.FileType}
              category={file.FileCategory}
              className="w-5 h-5 flex-shrink-0"
            />
            {/* The format is stated ONCE, in the Type badge — the name used to
                repeat it as a "(CSV)" suffix beside a glyph that already says
                the same thing. */}
            <span className="truncate text-fg-strong" title={formattedName}>
              {formattedName}
            </span>
          </div>
        ),
        Type: <Badge color={getKindBadgeColor(fileExt)} className="text-xs whitespace-nowrap font-medium">{fileExt}</Badge>,
        Tags: (
          <TagPicker
            tags={file.Tags || []}
            fileId={file.FileID}
            onTagsChanged={(tags) => handleTagsChanged({ fileId: file.FileID }, tags)}
          />
        ),
        'Last Modified': formatDateTime(file.ModifiedAt),
        ...(isSearching ? { Location: locationCell } : {}),
        'Has Access': accessCell,
        isFolder: false,
      };
    }
  });

  const tableActions = [
    {
      label: t('Rename'),
      onClick: (row: any) => row.isFolder ? handleRenameFolder(row.doc) : handleRenameFile(row.doc),
      icon: <PiPencilLight className="h-4 w-4" />,
      disabled: (row: any) => {
        if (isProtectedFolder(row)) return true;
        const isLocked = row.FolderType === 'Locked';
        return isLocked;
      },
    },
    // {
    //   label: t('Edit in Microsoft Word'),
    //   onClick: async (row: any) => {
    //     if (row.isFolder) return;
    //     if (!isMsSignedIn) await MSALsignIn();
    //     try {
    //       const fileName = row.doc.FileName?.endsWith('.docx') ? row.doc.FileName : `${row.doc.FileName}.docx`;
    //       const result = await dispatch(streamFile(row.doc.FileID, fileName, 'attachment', row.doc.FolderID) as any);
    //       if (!result?.success || !result.blob) return;
    //       const { fileID, webURL } = await uploadFileToOneDrive(fileName, result.blob, row.doc.SharedWith || []);
    //       const editLink = webURL || (await getEditLink(fileID));
    //       if (editLink) window.open(editLink, '_blank');
    //     } catch (err) {
    //       dispatch(showFeedbackModal({ modalType: 'error', message: t('Failed to open in Microsoft Word'), showModal: true, duration: 4000 }));
    //     }
    //   },
    //   icon: <PiMicrosoftWordLogoLight className="h-4 w-4" />,
    //   disabled: (row: any) => row.isFolder,
    // },
    // {
    //   label: t('Open with Google Docs'),
    //   onClick: async (row: any) => {
    //     if (row.isFolder) return;
    //     if (!isGoogleSignedIn) await googleSignIn();
    //     handleOpenPicker((pickedFile: any) => {
    //       if (pickedFile?.url) window.open(pickedFile.url, '_blank');
    //     });
    //   },
    //   icon: <PiGoogleLogoLight className="h-4 w-4" />,
    //   disabled: (row: any) => row.isFolder,
    // },
    {
      label: t('Manage Access'),
      onClick: (row: any) => handleManageAccess(row.doc, row.isFolder),
      icon: <PiUsersThreeLight className="h-4 w-4" />,
      disabled: (row: any) => isProtectedFolder(row),
    },
    {
      label: t('Activity'),
      onClick: (row: any) => {
        const isFolder = row.isFolder;
        const doc = row.doc;
        setAuditTarget({
          type: isFolder ? 'folder' : 'file',
          id: isFolder ? doc.FolderID : doc.FileID,
          name: isFolder ? doc.FolderName : doc.FileName,
        });
      },
      icon: <PiClockCounterClockwiseLight className="h-4 w-4" />,
    },
    {
      label: t('Delete'),
      onClick: (row: any) => row.isFolder ? handleDeleteFolder(row.doc) : handleDeleteFile(row.doc),
      icon: <PiTrashLight className="h-4 w-4" />,
      disabled: (row: any) => isProtectedFolder(row) || row.FolderType === 'Locked',
      isDestructive: true,
    },
  ];

  const handleSelectItem = (id: string) => {
    setSelectedItems(prev => prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]);
  };

  const selectableIds = tableData.map(row => row.id);
  const handleSelectAllItems = () => {
    setSelectedItems(selectedItems.length === selectableIds.length ? [] : selectableIds);
  };

  const handleRowClick = (row: any) => {
    row.isFolder ? handleFolderClick(row.doc) : handleFileClick(row.doc);
  };

  const handleCreateFolder = useCallback(() => {
    if (!checkSectionGate('files')) return;
    const params = new URLSearchParams(location.search);
    params.set('add', 'createFolder');
    history.push(`${location.pathname}?${params.toString()}`);
  }, [history, location]);

  const handleImportFiles = useCallback(() => {
    if (!checkSectionGate('files')) return;
    const params = new URLSearchParams(location.search);
    params.set('add', 'createFolderWithFiles');
    history.push(`${location.pathname}?${params.toString()}`);
  }, [history, location]);

  const handleCloseForm = useCallback(() => {
    const newParams = new URLSearchParams(location.search);
    newParams.delete('add');
    history.push(`${location.pathname}${newParams.toString() ? `?${newParams.toString()}` : ''}`);
  }, [history, location]);

  const handleLoadMore = useCallback(async () => {
    if (isLoadingMore || !pagination.has_next) return;

    const currentFolderId = getCurrentFolderId();

    try {
      setIsLoadingMore(true);
      const nextPage = pagination.page + 1;
      
      let res: FileApiResponse | undefined;

      if (currentFolderId) {
        const parentFolderId = getParentFolderId();
        res = await fetchFolderContents(currentFolderId, nextPage, parentFolderId, { searchTerm, sortBy, sortOrder });
      } else {
        // Root level load more
        res = await fetchRootFiles({ page: nextPage, searchTerm, sortBy, sortOrder });
      }

      if (res) {
        // Append interleaved items
        const newItems = res.items || [...(res.folders || res.subfolders || []), ...(res.files || [])];

        setDisplayedItems(prev => [...prev, ...newItems]);

        // Update pagination
        if (res.pagination) {
          setPagination({
            page: res.pagination.page || nextPage,
            limit: res.pagination.limit || 15,
            total_items: res.pagination.total_items || 0,
            total_pages: res.pagination.total_pages || 0,
            has_next: res.pagination.has_next || false,
            has_previous: res.pagination.has_previous || false,
          });
        }
      }
    } catch (err) {
      console.error('Error loading more:', err);
    } finally {
      setIsLoadingMore(false);
    }
  }, [pagination, isLoadingMore, getCurrentFolderId, getParentFolderId, searchTerm, sortBy, sortOrder]);

  const params = new URLSearchParams(location.search);
  const addFormType = params.get('add');

  // Build selectedFolderItem from pathStack so forms auto-select current folder
  const currentFolderItem = pathStack.length > 0
    ? { FolderID: pathStack[pathStack.length - 1].id, FolderName: pathStack[pathStack.length - 1].name }
    : null;

  // Handle forms
  // A fresh instance per open. The forms are raised by a query param, so a
  // second "create folder" without a full remount inherited the last run's
  // name, tags and collaborators; keying on the history entry that opened the
  // form guarantees new state every time.
  const formInstanceKey = (location as any).key || 'files-form';

  if (addFormType === 'createFolder') {
    return (
      <div className="flex flex-1 flex-col min-h-0 overflow-auto relative text-text-light dark:text-text-dark transition-colors duration-300">
        <CreateFolderForm key={formInstanceKey} onClose={handleCloseForm} existingFolders={displayedItems.filter(i => i.FolderName && !i.FileID)} selectedFolderItem={currentFolderItem} onRefresh={() => {}} />
      </div>
    );
  }

  if (addFormType === 'createFolderWithFiles') {
    return (
      <div className="flex flex-1 flex-col min-h-0 overflow-auto relative text-text-light dark:text-text-dark transition-colors duration-300">
        <CreateFolderWithFilesForm key={formInstanceKey} onClose={handleCloseForm} existingFolders={displayedItems.filter(i => i.FolderName && !i.FileID)} selectedFolderItem={currentFolderItem} onRefresh={() => {}} />
      </div>
    );
  }

  // Main render
  return (
    <div className="flex flex-1 flex-col min-h-0 overflow-auto relative text-fg-body transition-colors duration-300">
      {editorOpen && selectedFile ? (
        <WordEditor
          file={selectedFile.FileID}
          folderID={selectedFile.FolderID || getCurrentFolderId()}
          parentFolderID={selectedFile.ParentFolderID || getParentFolderId()}
          onClose={() => {
            setEditorOpen(false);
            setSelectedFile(null);
            // Remove file ID and type=file param from URL
            const pathSegments = location.pathname.split('/').filter(Boolean);
            pathSegments.pop(); // Remove file ID
            const folderPath = '/' + pathSegments.join('/');
            history.push(folderPath);
          }}
          setEditorOpen={setEditorOpen}
          onFileDeleted={() => {
            setSelectedFile(null);
            // Remove file ID and type=file param from URL
            const pathSegments = location.pathname.split('/').filter(Boolean);
            pathSegments.pop(); // Remove file ID
            const folderPath = '/' + pathSegments.join('/');
            history.push(folderPath);
          }}
          subscriptionActive={subscriptionActive}
          allFolderFiles={displayedItems.filter(i => i.FileID)}
        />
      ) : (
        <>
          {!clientDocs && (
            <Header
              subscriptionActive={subscriptionActive}
              actions={[
                {
                  label: t("Add Folder"),
                  icon: <PiPlusLight className="w-4 h-4" />,
                  onClick: handleCreateFolder,
                  disabled: false,
                  variant: 'outline'
                },
                {
                  label: t("Import Files"),
                  onClick: handleImportFiles,
                  icon: <PiCloudArrowUpLight className="w-4 h-4" />,
                  disabled: false,
                  variant: 'primary'
                },
              ]}
            />
          )}
          <div data-tour-id="files-toolbar">
          <FilterBarWithTabs
            itemCount={tableData.length}
            totalItemCount={pagination.total_items}
            itemCountLabel="items" sortOptions={[
            { value: 'name_asc', label: t('Name (A-Z)'), current: sortBy === 'name' && sortOrder === 'asc' },
            { value: 'name_desc', label: t('Name (Z-A)'), current: sortBy === 'name' && sortOrder === 'desc' },
            { value: 'created_at_asc', label: t('Created (Oldest)'), current: sortBy === 'created_at' && sortOrder === 'asc' },
            { value: 'created_at_desc', label: t('Created (Newest)'), current: sortBy === 'created_at' && sortOrder === 'desc' },
          ]} onSortChange={(value) => {
            const parts = value.split('_');
            const order = parts.pop() as 'asc' | 'desc';
            const field = parts.join('_');
            setSortBy(field);
            setSortOrder(order);
          }} onSearchChange={(query) => {
            // Debounce search to avoid excessive API calls
            if (searchDebounceRef.current) {
              clearTimeout(searchDebounceRef.current);
            }
            searchDebounceRef.current = setTimeout(() => {
              setSearchTerm(query);
            }, 400);
          }} sortValue={`${sortBy}_${sortOrder}`} actions={[
            ...(selectedItems.length > 0 && !showTrash ? [{
              label: t('Delete Selected'),
              onClick: handleDeleteSelected,
              icon: <PiTrashLight className="h-4 w-4" />,
            }] : []),
            ...(showTrash ? trashBulkActions : []),
            ...(showTrash ? [{
              label: t('Empty Recycle Bin'),
              onClick: () => emptyTrashRef.current?.(),
              icon: <PiRecycleFill className="h-4 w-4" />,
            }] : []),
          ]} selectionCount={showTrash ? trashSelectedCount : (selectedItems.length > 0 ? selectedItems.length : undefined)} recycleBinEnabled recycleBinActive={showTrash} recycleBinLabel={t('Files')} onRecycleBinToggle={(active) => { setShowTrash(active); if (!active) setRefreshKey(k => k + 1); }} onClearAllFilters={() => {
            setSearchTerm('');
            setSortBy('name');
            setSortOrder('asc');
          }} />
          </div>
          {showTrash ? (
            <Trash
              onClose={() => { setShowTrash(false); setRefreshKey(k => k + 1); }}
              emptyTrashRef={emptyTrashRef}
              onBulkActionsChange={setTrashBulkActions}
              onSelectedCountChange={setTrashSelectedCount}
            />
          ) : (
          <div data-tour-id="files-table" className="flex-1 min-h-0 overflow-auto relative">
            <div className="flex-1 overflow-x-auto overflow-y-auto">
            {/* First-load state is the table's OWN skeleton: it draws the real
                column count inside the real chrome, so nothing jumps when the
                rows land. */}
            <Table
              columns={columns}
              data={tableData}
              onRowClick={handleRowClick}
              actions={tableActions}
              isLoading={isLoading}
              enableSelection
              selectedIds={selectedItems}
              onSelect={handleSelectItem}
              onSelectAll={handleSelectAllItems}
              hasMore={pagination.has_next}
              isLoadingMore={isLoadingMore}
              onLoadMore={handleLoadMore}
              resizableColumns={true}
              defaultColumnWidths={{
                'Select': 48,
                'Name': isSearching ? 220 : 250,
                ...(isSearching ? { 'Location': 200 } : {}),
                'Type': 100,
                'Tags': isSearching ? 150 : 180,
                'Has Access': isSearching ? 150 : 180,
                'Last Modified': 160,
                'Actions': 48,
              }}
              sortableColumns={['Name', 'Last Modified']}
              sortBy={sortBy === 'modified_at' ? 'last_modified' : sortBy}
              sortOrder={sortOrder}
              onSort={(col, order) => {
                // Map Table column keys back to API sort fields
                const columnMap: Record<string, string> = { 'name': 'name', 'last_modified': 'modified_at' };
                setSortBy(columnMap[col] || col);
                setSortOrder(order);
              }}
              total_items={pagination.total_items}
              total_showing_items={tableData.length}
              setIsShowing={true}
              showingLabel={t('items')}
              emptyState={
                isSearching
                  ? {
                      icon: <PiMagnifyingGlassLight />,
                      headline: t('No matches'),
                      subtext: t('No files or folders match that search. Try a different term.'),
                    }
                  : {
                      icon: <PiFolderDashedLight />,
                      headline: t('No items'),
                      subtext: t('Create folders or import files.'),
                      button: {
                        label: t('Create / Import'),
                        onClick: handleImportFiles,
                      },
                    }
              }
            />
            </div>
          </div>
          )}

          <FeatureTour
            steps={filesListTourSteps}
            run={isTourRunning}
            onCallback={handleTourCallback}
            disableOverlay
          />

          {/* Audit Log Side Panel with Backdrop */}
          {auditTarget && (
            <ActivityDrawer onClose={() => setAuditTarget(null)}>
              <AuditLogPanel
                entityType={auditTarget.type}
                entityId={auditTarget.id}
                entityName={auditTarget.name}
                onClose={() => setAuditTarget(null)}
              />
            </ActivityDrawer>
          )}
        </>
      )}
    </div>
  );
};

export default Files;